/**
 * 개선된 분석 테이블 전용 믹스인
 * ★ 공통 상태는 selectedNode, plotLineDatetime, active, timeType 등을 관리
 * 1) selectedNode: 선택한 obj 노드 정보
 * 2) plotLineDatetime: 이력 데이터 기준 시각
 * 3) active: 컴포넌트 활성화 여부
 * 4) timeType: 시각 타입
 *
 * ★★ 데이터 로드 시 반드시 loadData function을 호출하도록 되어있음. 따라서 해당 믹스인을 참조하는 컴포넌트는 반드시 loadData를 오버라이드 해야함.
 *
 * ★★★ analysis-table 믹스인은 다음과 같이 설정하여 사용한다.
 * 1) 믹스인 참조: import AnalysisTableMixin from '@/views/performance/node/analysis/database/mixins/analysis-table';
 * 2) <w-perf-analysis-time-info../>  에 reference 설정
 *    > :ref="timeInfoRef"
 *    해당 reference를 설정해야 obj  변경 시 시간 타입이 자동으로 '이력'으로 리셋됨. 리셋 시 별도 로직으로 동작해야되는 경우 clearTimeType 함수를 오버라이드한다.
 * 3) <w-perf-analysis-time-info../>  의 @change="onTimeTypeChange" @reload="onCurrentDataReLoad"를 설정한다.
 *    믹스인에서 제공하는 함수를 이벤트 핸들러로 설정하여, 시간 타입 변경 또는 '현재'에서 데이터를 재조회하는 이벤트 발생 시 loadData를 호출하여 적절한 데이터가 조회되도록 한다.
 */
import isNil from 'lodash/isNil';
import { analysisTimeType } from '@/constants/performance';
import { getRequestDatetimeParams, getIntervalDatetimeParams } from '@/utils/date';

export default {
  props: {
    /**
     * 컴포넌트 활성화 여부
     */
    active: {
      type: Boolean,
    },
    /**
     * [운영 탭 추가] 분석하위 컴포넌트 함께 사용함에 따라 기능분기를 위해 추가
     */
    operationFlag: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      /**
       * 분석 시간 정보 컴포넌트 reference
       * @type {String}
       */
      timeInfoRef: 'timeInfo',
      /**
       * obj 변경 여부.
       * 시간 타입 초기화 여부 판단 시 사용.
       * @type {Boolean}
       */
      changeObjFlag: false,
      /**
       * 현재 선택된 시간 타입
       * @type {String}
       */
      timeType: null,
      analysisTimeType,
    };
  },
  computed: {
    /**
     * 선택한 obj 노드 정보
     * @author Ju
     * @return {[type]} [description]
     */
    selectedNode() {
      return this.$store.getters.selectedNode;
    },
    /**
     * 이력 데이터 기준 시각
     * @author Ju
     * @return {[type]} [description]
     */
    plotLineDatetime() {
      return this.$store.getters.plotLineDatetime;
    },
  },
  watch: {
    selectedNode: {
      immediate: true,
      handler() {
        if (this.active) {
          this.changeObjFlag = true;
        }
      },
    },
    plotLineDatetime: {
      immediate: true,
      handler() {
        this.loadAnalysisTable();
      },
    },
    active: {
      // immediate: true,
      handler() {
        this.loadAnalysisTable();
      },
    },
  },
  methods: {
    /**
     * 시간 파라미터를 반환한다.
     * @author Ju
     * @param  {[type]}  plotLineDatetime [description]
     * @param  {Boolean} isLog            [description]
     * @return {[type]}                   [description]
     */
    getDatetimeParams(plotLineDatetime, isLog) {
      const baselineDatetime = isNil(plotLineDatetime) ? new Date().getTime() : plotLineDatetime;
      const endDatetime = getRequestDatetimeParams(baselineDatetime);
      const startDatetime = isLog ? getIntervalDatetimeParams(baselineDatetime, 'hour', 24) : getIntervalDatetimeParams(baselineDatetime, 'minute', 15);
      return {
        startDatetime,
        endDatetime,
      };
    },
    /**
     * 분석 데이터를 로드한다.
     * @author Ju
     * @return {Boolean} [description]
     */
    loadAnalysisTable() {
      /**
       * [운영 탭 추가] 차트를 사용하지않아 운영탭에서는 분석 탭 기능 return 처리
       */
      if (isNil(this.plotLineDatetime) || !this.active || isNil(this.selectedNode) || this.operationFlag) {
        return;
      }
      if (this.changeObjFlag) {
        this.clearTimeType();
        this.$nextTick(() => {
          this.loadData();
          this.changeObjFlag = false;
        });
      } else {
        if (isNil(this.timeType)) {
          this.clearTimeType();
        }
        this.$nextTick(() => {
          this.loadData();
        });
      }
    },
    /**
     * 시간 타입을 기본 시간 타입으로 초기화한다. 필요시 오버라이드한다.
     * 테이블 타입인 현재, 이력 값은 기준정보로 관리되도록 수정 (analysis_timetype)(2020. 02. 18 - Song)
     *
     * @author Ju
     * @return {[type]} [description]
     */
    clearTimeType() {
      if (this.$refs[this.timeInfoRef] && isNil(this.$refs[this.timeInfoRef].fixedTimeType)) {
        this.$refs[this.timeInfoRef].setTimeType(this.analysisTimeType[this.$store.state.common.analysisTimetype].value);
        this.timeType = this.analysisTimeType[this.$store.state.common.analysisTimetype].value;
      }
    },
    /**
     * 시간 타입 변경 이벤트 핸들러
     * @author Ju
     */
    onTimeTypeChange(value) {
      this.timeType = value;
      this.loadData();
    },
    /**
     * 현재 데이터 reload 이벤트 핸들러
     * @author Ju
     * @return {[type]} [description]
     */
    onCurrentDataReLoad() {
      this.loadData();
    },
    /**
     * 데이터 로드 함수. 해당 믹스인을 참조할 경우 반드시 해당 함수를 오버라이드 해야한다.
     * @author Ju
     * @return {[type]} [description]
     */
    loadData() {}, // override
  },
};



// WEBPACK FOOTER //
// ./src/mixins/performance/analysis-table-ex.js